import { Link } from 'react-router';
import { formatPeso } from '@tahi/core';
import { Money, ScreenHeader } from '../../../ui';
import { dayHeading, monthLong } from '../../spending/format';
import { useToday } from '../../spending/hooks';
import type { ShoppingTrip } from '../../../data/repos/shopping';
import { copy, LIST_BACK } from '../copy';
import { usePriceData, useTrips } from '../hooks';
import { CARD, FOCUS, LABEL, SCREEN } from '../styles';

/** Past trips by date with totals, grouped by month (spec §6). */
export function PastTripsScreen() {
  const trips = useTrips();
  const data = usePriceData();
  const today = useToday();

  const counts = new Map<string, number>();
  for (const l of data?.lines ?? []) counts.set(l.tripId, (counts.get(l.tripId) ?? 0) + 1);

  const months: { month: string; trips: ShoppingTrip[] }[] = [];
  for (const t of trips ?? []) {
    const month = t.date.slice(0, 7);
    const last = months[months.length - 1];
    if (last?.month === month) last.trips.push(t);
    else months.push({ month, trips: [t] });
  }

  return (
    <main className={SCREEN}>
      <ScreenHeader title={copy.pastTrips} back={LIST_BACK} />
      {trips === undefined ? (
        <p className="text-muted">{copy.loading}</p>
      ) : trips.length === 0 ? (
        <p className="text-muted">{copy.noTrips}</p>
      ) : (
        months.map((m) => (
          <section key={m.month} aria-label={monthLong(m.month)} className="flex flex-col gap-2">
            <h2 className="flex items-baseline justify-between gap-2">
              <span className={LABEL}>{monthLong(m.month)}</span>
              <span className="text-sm text-muted">
                {copy.monthTotal(m.trips.reduce((s, t) => s + t.totalMinor, 0))}
              </span>
            </h2>
            <ul className={`${CARD} flex flex-col divide-y divide-divider py-1`}>
              {m.trips.map((t) => {
                const day = dayHeading(t.date, today);
                const n = counts.get(t.id) ?? 0;
                return (
                  <li key={t.id}>
                    <Link
                      to={`/m/shopping/trips/${t.id}`}
                      aria-label={`${copy.tripCard(day, t.store, formatPeso(t.totalMinor))}, ${copy.items(n)}`}
                      className={`flex min-h-12 items-center justify-between gap-3 rounded-xl py-2 ${FOCUS}`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-fg">{t.store ?? day}</span>
                        <span className="block truncate text-xs text-muted">
                          {t.store ? `${day} · ` : ''}
                          {copy.items(n)}
                        </span>
                      </span>
                      <Money
                        minor={t.totalMinor}
                        cents="auto"
                        animate={false}
                        className="shrink-0 text-fg"
                      />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
    </main>
  );
}
